import React, { useEffect, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import { getEmailLogs } from "@services/email-template.service";
import { QueryRequestHelper } from "@common/query-request-helper";
import { useNavigate } from "react-router-dom";
import Notfound from "@components/Not-Found";
import PaginationPage from "@components/pagination/pagination";
import SearchHeader from "@components/common/SearchHeader";
import NoAccessPage from "@components/common/NoAccess";
import SkeletonTable from "@components/common/Skeleton/skeleton-table";
import EmailLogsTable from "./table";
import DatePicker from "react-datepicker";
import moment from "moment";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import EmailLogViewPopup from "./view-popup";

export default function EmailLogs(props: any) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const navigate = useNavigate();
  const [emailLogsList, setEmailLogsList] = useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  /* -------------------------------------------------------------------------- */
  /*                              Date Routes                                   */
  /* -------------------------------------------------------------------------- */
  const [from_date, setFirstDay] = React.useState(new Date());
  const [to_date, setLastDay] = React.useState(new Date());

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0 && props.permissionAPITriggered) {
      const currentMonth = moment().startOf("month");
      const firstDay: any = currentMonth.format("YYYY-MM-DD");
      const lastDay: any = currentMonth.endOf("month").format("YYYY-MM-DD");
      setFirstDay(new Date(firstDay));
      setLastDay(new Date(lastDay));
      getData();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0 && props.permissionAPITriggered) {
      const currentMonth = moment().startOf("month");
      const firstDay: any = currentMonth.format("YYYY-MM-DD");
      const lastDay: any = currentMonth.endOf("month").format("YYYY-MM-DD");
      let from = params.from_date
        ? new Date(params.from_date)
        : new Date(firstDay);
      let to = params.to_date
        ? new Date(params.to_date)
        : new Date(lastDay);
      setFirstDay(from);
      setLastDay(to);
      getData();
      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }
      if (params.search_text) {
        setSearchInput(params.search_text);
        setShowSearchTextbox(true);
      }
      if (params.limit) {
        setLimit(parseInt(params.limit));
      }
    }
  }, [window.location.href, props.permissionAPITriggered]);

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    try {
      // Get date range from URL params or use current month as default
      let fromDate, toDate;
      
      if (params.from_date && params.to_date) {
        // Use dates from URL params
        fromDate = params.from_date;
        toDate = params.to_date;
      } else {
        // Use current month as default
        const currentMonth = moment().startOf("month");
        fromDate = currentMonth.format("YYYY-MM-DD");
        toDate = currentMonth.endOf("month").format("YYYY-MM-DD");
      }

      let payload = {
        page_no: params.page_no ? parseInt(params.page_no) : 1,
        page_size: params.limit ? parseInt(params.limit) : limit,
        search_text: params.search_text,
        from_date: fromDate,
        to_date: toDate,
      };
      
      let queryRequest = QueryRequestHelper(payload);
      setIsLoading(true);
      let resp = await getEmailLogs(queryRequest);
      if (resp) {
        setEmailLogsList(getValue(resp, `data.result`, []));
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                              Date Handlers                                 */
  /* -------------------------------------------------------------------------- */
  const handleDate = (date: any) => {
    setFirstDay(date[0]);
    setLastDay(date[1]);
  };

  const handleSubmitDate = () => {
    let first = moment(from_date).format("YYYY-MM-DD");
    let last = moment(to_date).format("YYYY-MM-DD");
    if (first && last && first !== "Invalid date" && last !== "Invalid date") {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      let payload: any = {
        ...params,
        from_date: first,
        to_date: last,
        page_no: 1,
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
    } else {
      toast.error("Please select valid date range");
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);

    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    setSearchInput(e.target.value);
    let payload: any = {
      ...params,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               View Modal Section                           */
  /* -------------------------------------------------------------------------- */
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [selectedEmailLog, setSelectedEmailLog] = useState({});

  const handleRowClick = (emailLog: any) => {
    setSelectedEmailLog(emailLog);
    setShowViewPopup(true);
  };

  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      <>
        <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
          <h4 className="dashbaord-wrapper__header-title">Email Logs</h4>
          <div>
            <h3 className="dashboard-compliance__date dashboard-compliance__date--compliance overview-wrapper__header-date d-flex align-items-center">
              <div className="d-flex">
                <DatePicker
                  selectsRange={true}
                  startDate={from_date}
                  endDate={to_date}
                  className="react-date-picker__inputGroup__input_dashboard"
                  onChange={(update: any) => {
                    handleDate(update);
                  }}
                  dateFormat="d MMM yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
                <button
                  className="dashboard-wrapper__apply-btn"
                  onClick={handleSubmitDate}
                >
                  Apply
                </button>
              </div>
            </h3>
          </div>
        </div>
        <div className="admin-wrapper position-relative">
          <div className="admin-wrapper position-relative">
            <SearchHeader
              searchInput={search_text}
              showSearchTextbox={showSearchTextbox}
              toggleSearch={toggleSearch}
              toggleSearchClose={toggleSearchClose}
              handleChangeSearch={handleChangeSearch}
              add="false"
            />
          </div>
        </div>
        <div
          className="datatable_main_container"
          style={{ overflowY: isLoading ? "hidden" : "auto" }}
        >
          {!isLoading &&
          !getValue(props, `subTabPermissionList`, []).includes(
            "admin_user_controls_email_logs"
          ) ? (
            <NoAccessPage />
          ) : isLoading || !props.permissionAPITriggered ? (
            <SkeletonTable />
          ) : (
            <>
              {getValue(emailLogsList, `length`, 0) > 0 ? (
                <>
                  <EmailLogsTable
                    emailLogsList={emailLogsList}
                    onRowClick={handleRowClick}
                  />
                </>
              ) : (
                <>
                  {Object.keys(params).length !== 0 ? (
                    <Notfound />
                  ) : (
                    <Notfound />
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="fixed_footer_wrapper d-flex justify-content-between">
          {emailLogsList.length > 0 && (
            <img
              src="/images/building-illustration.svg"
              className="img-fluid"
            />
          )}
          {totalCount > 10 ? (
            <PaginationPage
              totalCount={totalCount}
              limit={limit}
              pageNumber={page_no}
              handleChangePagination={handleChangePagination}
              handleChangeLimit={handleChangeLimit}
            />
          ) : (
            <div className="show-rows-count mt-3 d-flex">
              Total Count:&nbsp;&nbsp;{" "}
              <span style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</span>
            </div>
          )}
        </div>

        <EmailLogViewPopup
          showUpdatePopup={showViewPopup}
          setShowUpdatePopup={setShowViewPopup}
          editInfo={selectedEmailLog}
        />
      </>
    </DashboardLayout>
  );
}